import Link from 'next/link';
import dayjs from 'dayjs';

import { CallToActionLink, toCompleteCallToAction } from './CallToActionLink';
import { CategoryLabel } from './CategoryLabel';
import { PlatformTag } from './PlatformTag';
import { SCREENSHOT_PANEL_CLASS_NAME, Screenshot } from './Screenshot';
import {
  BETA_BUTTON_CLASS_NAME,
  PRIMARY_BUTTON_CLASS_NAME,
  TEXT_LINK_CLASS_NAME
} from './styles';
import {
  BETA_CATEGORY,
  buildProductUpdatePath
} from '@lib/sanity/productUpdate';
import { updateDate } from '@lib/whats-new/model';
import type { SanityProductUpdateSummary } from '@lib/sanity/types';

export const UPDATE_DATE_FORMAT = 'D MMMM YYYY';

const IMAGE_WIDTHS = [320, 480, 640, 960];
const IMAGE_DEFAULT_WIDTH = 640;
const IMAGE_SIZES: Record<UpdateCardVariant, { phone: string; wide: string }> =
  {
    featured: { phone: '160px', wide: '(min-width: 48rem) 400px, 90vw' },
    compact: {
      phone: '90px',
      wide: '(min-width: 64rem) 300px, (min-width: 40rem) 45vw, 90vw'
    }
  };

// A fixed panel keeps every card the same height, whatever shape of
// screenshot it holds.
const IMAGE_PANEL_CLASS_NAMES: Record<UpdateCardVariant, string> = {
  featured: 'h-[300px] p-6 md:h-auto md:min-h-[380px] md:p-8',
  compact: 'h-[190px] p-4'
};
const IMAGE_MAX_HEIGHT_CLASS_NAMES: Record<UpdateCardVariant, string> = {
  featured:
    '[--screenshot-max-height:252px] md:[--screenshot-max-height:316px]',
  compact: '[--screenshot-max-height:158px]'
};

/**
 * `featured` is a large card with the image beside the words, for the newest
 * updates. `compact` is a smaller card for the grid of earlier updates.
 */
export type UpdateCardVariant = 'featured' | 'compact';

export const UpdateMeta = ({
  update
}: {
  update: SanityProductUpdateSummary;
}) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 font-body text-[13px] text-landing-slate">
    <CategoryLabel category={update.category} />
    <PlatformTag platform={update.platform} />
    <time dateTime={updateDate(update)}>
      {dayjs(updateDate(update)).format(UPDATE_DATE_FORMAT)}
    </time>
  </div>
);

export const UpdateCard = ({
  update,
  variant
}: {
  update: SanityProductUpdateSummary;
  variant: UpdateCardVariant;
}) => {
  const path = buildProductUpdatePath(update.slug);
  const isFeatured = variant === 'featured';
  const callToAction = isFeatured
    ? toCompleteCallToAction(update.callToAction)
    : null;
  const image = update.featuredImage?.asset ? update.featuredImage : null;

  return (
    <article
      className={`overflow-hidden rounded-2xl border border-landing-hairline bg-white ${
        isFeatured && image
          ? 'md:grid md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]'
          : 'flex flex-col'
      }`}
    >
      {image && (
        // The title links to the same page, so the picture stays out of the
        // tab order.
        <Link
          href={path}
          tabIndex={-1}
          aria-hidden="true"
          className={`flex items-center justify-center ${SCREENSHOT_PANEL_CLASS_NAME} ${IMAGE_PANEL_CLASS_NAMES[variant]}`}
        >
          <Screenshot
            image={image}
            alt=""
            scale="card"
            widths={IMAGE_WIDTHS}
            defaultWidth={IMAGE_DEFAULT_WIDTH}
            sizes={IMAGE_SIZES[variant]}
            loading={isFeatured ? undefined : 'lazy'}
            maxHeightClassName={IMAGE_MAX_HEIGHT_CLASS_NAMES[variant]}
          />
        </Link>
      )}
      <div
        className={`flex flex-1 flex-col gap-3 ${
          isFeatured ? 'p-6 sm:p-8' : 'p-5'
        }`}
      >
        <UpdateMeta update={update} />
        {isFeatured ? (
          <h2 className="m-0 font-heading text-2xl font-extrabold leading-[1.2] tracking-[-0.01em] text-landing-ink [text-wrap:balance] sm:text-[28px]">
            <Link
              href={path}
              className="font-heading font-extrabold text-landing-ink no-underline hover:underline"
            >
              {update.title}
            </Link>
          </h2>
        ) : (
          <h3 className="m-0 font-heading text-lg font-extrabold leading-[1.3] text-landing-ink [text-wrap:balance]">
            <Link
              href={path}
              className="font-heading font-extrabold text-landing-ink no-underline hover:underline"
            >
              {update.title}
            </Link>
          </h3>
        )}
        {update.standfirst && (
          <p
            className={`m-0 font-body leading-[1.6] text-[#33475a] ${
              isFeatured ? 'text-base' : 'line-clamp-3 text-[15px]'
            }`}
          >
            {update.standfirst}
          </p>
        )}
        <div className="mt-auto flex flex-wrap items-center gap-x-5 gap-y-3 pt-2">
          {callToAction && (
            <CallToActionLink
              callToAction={callToAction}
              className={
                update.category === BETA_CATEGORY
                  ? BETA_BUTTON_CLASS_NAME
                  : PRIMARY_BUTTON_CLASS_NAME
              }
            />
          )}
          <Link href={path} className={TEXT_LINK_CLASS_NAME}>
            Read the update
          </Link>
        </div>
      </div>
    </article>
  );
};
